import React, { useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
   addStripePaymentMethodAsync,
   setStripeError,
   setStripeFormModalOpen,
   setStripeLoading,
   stripeStore,
} from '../../stores/stripe.slice';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js/dist/stripe-js/stripe';
import CommonDialog from 'components/CommonDialog';
import { LoadStatus } from 'config/utils';
import { createStripeIntent } from '../../services/stripe.api';
import { accountStore } from 'features/account/stores/account.slice';
import './FinixFormModal.scss';

const StripeFormModal = () => {
   const dispatch = useAppDispatch();
   const stripe = useStripe();
   const elements = useElements();
   const formRef = useRef<HTMLFormElement>(null);
   const { fetchBillingProfilesStatus } = useAppSelector(accountStore);
   const { stripeFormModalOpen, addStripeBillingStatus, stripeLoading, stripeError } = useAppSelector(stripeStore);
   const isLoading =
      !stripe ||
      stripeLoading === LoadStatus.loading ||
      addStripeBillingStatus === LoadStatus.loading ||
      fetchBillingProfilesStatus === LoadStatus.loading;

   const handleClose = () => {
      dispatch(setStripeFormModalOpen(false));
   };

   const handleError = (error: StripeError) => {
      dispatch(setStripeLoading(LoadStatus.idle));
      dispatch(setStripeError(error.message));
   };

   const onStripeSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!stripe || !elements) {
         return;
      }

      dispatch(setStripeLoading(LoadStatus.loading));

      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
         handleError(submitError);
         return;
      }

      // Create the SetupIntent and obtain clientSecret
      const res = await createStripeIntent();

      if (res.success && res.customerClientSecret) {
         // Confirm the SetupIntent using the details collected by the Payment Element
         const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            clientSecret: res.customerClientSecret,
            confirmParams: {
               payment_method_data: { allow_redisplay: 'always' },
               return_url: `${window.location.origin}/checkout`,
            },
            redirect: 'if_required',
         });

         if (error) {
            handleError(error);
         } else if (setupIntent?.payment_method) {
            dispatch(
               addStripePaymentMethodAsync({
                  bodyInput: {
                     paymentMethod: setupIntent.payment_method,
                     acctType: 'credit',
                     onCheckout: true,
                  },
               }),
            );
         } else {
            dispatch(setStripeError('Error adding payment method'));
         }
      } else if (res.error && res.error.length > 0 && res.error[0].errorMessage) {
         dispatch(setStripeError(res.error[0].errorMessage));
      }

      dispatch(setStripeLoading(LoadStatus.idle));
   };

   const handleExternalSubmit = () => {
      formRef.current?.requestSubmit(); // Triggers onSubmit
   };

   return (
      <CommonDialog
         open={stripeFormModalOpen}
         onClose={handleClose}
         title="Add New Payment"
         onSubmit={handleExternalSubmit}
         isLoading={isLoading}
         submitLabel="Submit"
      >
         <form id="add-stripe-payment" ref={formRef} onSubmit={onStripeSubmit}>
            <PaymentElement />
            {stripeError && <div>{stripeError}</div>}
         </form>
      </CommonDialog>
   );
};

export default StripeFormModal;
